import { useId } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { formatTimestamp } from '@/app/lib/formatTimestamp';
import { LoadingBubbles } from '@/app/Components/LoadingBubbles';
import { getRoutePresentationDataAttributes, type RouteLayoutContract } from '@/app/lib/routePresentation';

export type StatusTone = 'neutral' | 'positive' | 'warning' | 'critical' | 'info' | 'success' | 'danger' | 'information';
export type DataStateKind = 'loading' | 'initial-loading' | 'refreshing' | 'empty' | 'stale' | 'stale-cache' | 'error' | 'hard-failure' | 'success' | 'partial' | 'permission';
export type AcademicDecisionFeedbackState = 'draft' | 'saved' | 'available' | 'completed' | 'at-risk' | 'conflict' | 'blocked' | 'stale' | 'error';

const operationalStateLabels: Record<DataStateKind, string> = {
  loading: 'Loading',
  'initial-loading': 'Loading',
  refreshing: 'Refreshing',
  empty: 'No results',
  stale: 'May be stale',
  'stale-cache': 'Cached result may be stale',
  error: 'Needs attention',
  'hard-failure': 'Unavailable',
  success: 'Updated',
  partial: 'Partial data',
  permission: 'Permission needed',
};

export const academicDecisionFeedbackStateMeta: Record<AcademicDecisionFeedbackState, {
  label: string;
  tone: 'neutral' | 'positive' | 'warning' | 'critical';
  toastType: 'success' | 'error' | 'info' | 'warning';
}> = {
  draft: { label: 'Draft', tone: 'neutral', toastType: 'info' },
  saved: { label: 'Saved', tone: 'positive', toastType: 'success' },
  available: { label: 'Available', tone: 'positive', toastType: 'success' },
  completed: { label: 'Completed', tone: 'positive', toastType: 'success' },
  'at-risk': { label: 'At risk', tone: 'warning', toastType: 'warning' },
  conflict: { label: 'Conflict', tone: 'critical', toastType: 'error' },
  blocked: { label: 'Blocked', tone: 'critical', toastType: 'error' },
  stale: { label: 'May be stale', tone: 'warning', toastType: 'warning' },
  error: { label: 'Error', tone: 'critical', toastType: 'error' },
};

const normalizedTone: Record<StatusTone, 'neutral' | 'positive' | 'warning' | 'critical' | 'info'> = {
  neutral: 'neutral', positive: 'positive', success: 'positive', warning: 'warning', critical: 'critical', danger: 'critical', info: 'info', information: 'info',
};

export type OperationalPageProps = HTMLAttributes<HTMLDivElement> & {
  /** Isolated design-system fixtures may demonstrate a non-route contract. */
  specimenPresentation?: RouteLayoutContract;
};

export function OperationalPage({ className = '', specimenPresentation, ...props }: OperationalPageProps) {
  return (
    <div
      {...props}
      className={`operational-page ${className}`}
      data-ss-presentation-source={specimenPresentation ? 'specimen' : 'route'}
      {...(specimenPresentation ? getRoutePresentationDataAttributes(specimenPresentation) : {})}
    />
  );
}

export function OperationalHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="operational-header">
      <div className="min-w-0">
        {eyebrow ? <p className="operational-eyebrow">{eyebrow}</p> : null}
        <h1 className="operational-title">{title}</h1>
        {description ? <div className="operational-description">{description}</div> : null}
      </div>
      {actions ? <div className="operational-actions">{actions}</div> : null}
    </header>
  );
}

/** A compact orientation pattern for routes that do not need the full page header. */
export const PageOrientation = OperationalHeader;

export function Surface({ children, className = '', variant = 'card', ...props }: HTMLAttributes<HTMLElement> & { children: ReactNode; variant?: 'card' | 'inset' | 'floating' | 'feature' }) {
  return <section className={`operational-surface ${className}`} data-variant={variant} {...props}>{children}</section>;
}

export function OperationalSection({
  title,
  description,
  actions,
  children,
  className = '',
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`operational-section ${className}`}>
      {title || description || actions ? (
        <div className="operational-section-header">
          <div className="min-w-0">
            {title ? <h2 className="operational-section-title">{title}</h2> : null}
            {description ? <p className="operational-section-description">{description}</p> : null}
          </div>
          {actions ? <div className="operational-actions">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function OperationalStatus({ tone = 'neutral', children, icon, className = '' }: { tone?: StatusTone; children: ReactNode; icon?: ReactNode; className?: string }) {
  return <span className={`operational-status ${className}`} data-tone={normalizedTone[tone]}>{icon ? <span aria-hidden="true">{icon}</span> : null}{children}</span>;
}

export const Status = OperationalStatus;

/** A comparable fact with an optional semantic signal; appropriate for small dashboard summaries. */
export function OperationalMetric({
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tone?: StatusTone;
}) {
  return (
    <div className="operational-metric" data-tone={normalizedTone[tone]}>
      <p className="operational-metric-label">{label}</p>
      <p className="operational-metric-value">{value}</p>
      {detail ? <p className="operational-metric-detail">{detail}</p> : null}
    </div>
  );
}

/** A responsive, aligned result row for courses, places, plans, and other operational choices. */
export function OperationalResult({
  children,
  actions,
  className = '',
}: {
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <article className={`operational-result ${className}`}>
      <div className="operational-result-summary">{children}</div>
      {actions ? <div className="operational-result-actions">{actions}</div> : null}
    </article>
  );
}

/**
 * A compact, presentational card for a named course, place, plan, or person.
 * Keep fetching, navigation, and action behavior in the route that renders it.
 */
export function EntityCard({
  title,
  eyebrow,
  metadata,
  status,
  children,
  action,
  className = '',
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  metadata?: ReactNode;
  status?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const headingId = useId();
  return (
    <article className={`rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4 text-[var(--ss-text)] sm:p-5 ${className}`} aria-labelledby={headingId}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {eyebrow ? <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ss-text-muted)]">{eyebrow}</p> : null}
          <h3 id={headingId} className="mt-1 min-w-0 max-w-full text-base font-semibold">{title}</h3>
          {metadata ? <div className="mt-1 text-sm text-[var(--ss-text-muted)]">{metadata}</div> : null}
        </div>
        {status ? <div className="shrink-0">{status}</div> : null}
      </div>
      {children ? <div className="mt-4 text-sm leading-6 text-[var(--ss-text-muted)]">{children}</div> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </article>
  );
}

/** Normal-flow filter framing; detailed controls remain owned by the calling route. */
export function OperationalFilter({ label, children, className = '' }: { label?: ReactNode; children: ReactNode; className?: string }) {
  return <div className={`operational-filter ${className}`}>{label ? <span className="operational-filter-label">{label}</span> : null}{children}</div>;
}

/** Evidence and source context that must remain readable across route accents and themes. */
export function OperationalEvidence({ children, tone = 'info', className = '', ...props }: HTMLAttributes<HTMLElement> & { children: ReactNode; tone?: StatusTone }) {
  return <aside {...props} className={`operational-evidence ${className}`} data-tone={normalizedTone[tone]}>{children}</aside>;
}

export function OperationalDataRow({
  label,
  value,
  detail,
}: {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <div className="operational-data-row grid grid-cols-[minmax(5.5rem,7.5rem)_minmax(0,1fr)] items-start gap-x-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
      <div className="min-w-0">
        <div className="operational-data-label whitespace-nowrap">{label}</div>
        {detail ? <div className="operational-data-detail">{detail}</div> : null}
      </div>
      <div className="operational-data-value min-w-0 max-w-full overflow-hidden">{value}</div>
    </div>
  );
}

export function OperationalState({
  kind,
  title,
  description,
  action,
  label,
}: {
  kind: DataStateKind;
  label?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="operational-state" data-kind={kind} role={kind === 'error' ? 'alert' : 'status'} aria-live={kind === 'error' ? 'assertive' : 'polite'} aria-busy={kind === 'loading' || kind === 'initial-loading' || undefined}>
      <div className="operational-state-main">
        {kind === 'loading' || kind === 'initial-loading' ? <LoadingBubbles size="compact" /> : null}
        <div className="operational-state-copy">
          <span className="operational-state-kicker">{label ?? operationalStateLabels[kind]}</span>
          <p className="operational-state-title">{title}</p>
          {description ? <p className="operational-state-description">{description}</p> : null}
        </div>
      </div>
      {action ? <div className="operational-actions">{action}</div> : null}
    </div>
  );
}

export const DataState = OperationalState;

/** Only string timestamps can be formatted; nodes pass through untouched. */
function formatProvenanceTime(value: ReactNode): ReactNode {
  return typeof value === 'string' ? formatTimestamp(value) : value;
}

export function OperationalSource({
  source,
  updatedAt,
  retrievedAt,
  stale = false,
  kind = 'cached',
}: {
  source: ReactNode;
  /** @deprecated Prefer retrievedAt when the time is a source retrieval time. */
  updatedAt?: ReactNode;
  retrievedAt?: ReactNode;
  stale?: boolean;
  kind?: 'live' | 'cached' | 'generated' | 'checked-in' | 'user-provided';
}) {
  return (
    <div className="operational-source" data-stale={stale || undefined} data-kind={kind}>
      <span className="operational-source-kind">{kind === 'checked-in' ? 'Checked-in snapshot' : kind === 'user-provided' ? 'User provided' : kind}</span>
      <span>Source: {source}</span>
      {retrievedAt ? <span>Retrieved {formatProvenanceTime(retrievedAt)}</span> : updatedAt ? <span>Updated {formatProvenanceTime(updatedAt)}</span> : null}
      {stale ? <span className="font-semibold">May be stale</span> : null}
    </div>
  );
}

export const Provenance = OperationalSource;

/**
 * A named academic object with the decision state, supporting context, and a
 * clear next action. It is intentionally presentational: routes retain their
 * own data loading, persistence, and action behavior.
 */
export function AcademicDecisionFeedback({
  academicObject,
  state,
  description,
  provenance,
  evidence,
  impact,
  nextAction,
  className = '',
}: {
  academicObject: ReactNode;
  state: AcademicDecisionFeedbackState;
  description?: ReactNode;
  provenance?: ReactNode;
  evidence?: ReactNode;
  impact?: ReactNode;
  nextAction?: ReactNode;
  className?: string;
}) {
  const headingId = useId();
  const stateMeta = academicDecisionFeedbackStateMeta[state];

  return (
    <article className={`academic-decision-feedback ${className}`} data-state={state} aria-labelledby={headingId}>
      <div className="academic-decision-feedback-header">
        <div className="min-w-0">
          <h3 id={headingId} className="academic-decision-feedback-object">{academicObject}</h3>
          {description ? <p className="academic-decision-feedback-description">{description}</p> : null}
        </div>
        <OperationalStatus tone={stateMeta.tone}>{stateMeta.label}</OperationalStatus>
      </div>
      {impact ? <div className="academic-decision-feedback-impact"><span>Impact</span><div>{impact}</div></div> : null}
      {evidence ? <OperationalEvidence className="academic-decision-feedback-evidence" tone={stateMeta.tone}>{evidence}</OperationalEvidence> : null}
      {provenance ? <div className="academic-decision-feedback-provenance" aria-label="Provenance">{provenance}</div> : null}
      {nextAction ? <div className="academic-decision-feedback-next-action"><span>Next action</span><div>{nextAction}</div></div> : null}
    </article>
  );
}
